import { useEffect, useMemo, useState } from 'react';
import api from '../api';
import DOMPurify from 'dompurify';
import { marked } from 'marked';

export default function KnowledgeBase() {
  const [cats, setCats] = useState([]);
  const [items, setItems] = useState([]);
  const [q, setQ] = useState('');
  const [filter, setFilter] = useState({ categoryId: '', type: '' });
  const [active, setActive] = useState(null);
  const [votes, setVotes] = useState({}); // { [entryId]: 'yes' | 'no' }
  const [sort, setSort] = useState('newest'); // newest | mostYes | bestRatio
  const [openFaq, setOpenFaq] = useState({}); // { [id]: true }

  const load = async () => {
    const c = await api.get('/kb/categories').then(r => r.data.items || []);
    setCats(c);
    const params = new URLSearchParams();
    if (q) params.set('q', q);
    if (filter.categoryId) params.set('categoryId', filter.categoryId);
    if (filter.type) params.set('type', filter.type);
    const i = await api.get(`/kb/entries?${params.toString()}`).then(r => r.data.items || []);
    setItems(i);
  };
  useEffect(()=>{ load(); }, [q, filter.categoryId, filter.type]);

  // Sorting
  const sortedItems = useMemo(() => {
    const arr = [...items];
    const ratio = (e) => {
      const y = e.helpfulYes || 0, n = e.helpfulNo || 0; const t = y+n; return t>0 ? y/t : 0;
    };
    if (sort === 'mostYes') arr.sort((a,b)=> (b.helpfulYes||0) - (a.helpfulYes||0));
    else if (sort === 'bestRatio') arr.sort((a,b)=> (ratio(b) - ratio(a)) || ((b.helpfulYes||0)-(a.helpfulYes||0)));
    else arr.sort((a,b)=> new Date(b.createdAt) - new Date(a.createdAt));
    return arr;
  }, [items, sort]);

  const openEntry = async (e) => {
    setActive(e);
    await api.post(`/kb/entries/${e.id}/view`);
    try {
      const r = await api.get(`/kb/entries/${e.id}/vote`).then(res => res.data);
      if (r && r.vote) setVotes(prev => ({ ...prev, [e.id]: r.vote }));
    } catch {}
  };
  const vote = async (e, v) => {
    try {
      const res = await api.post(`/kb/entries/${e.id}/helpful`, { vote: v });
      const { helpfulYes, helpfulNo, userVote } = res.data || {};
      // Update local active entry counts if it's the same
      setActive(prev => {
        if (!prev || prev.id !== e.id) return prev;
        return { ...prev, helpfulYes: helpfulYes ?? (v === 'yes' ? (prev.helpfulYes||0)+1 : prev.helpfulYes||0), helpfulNo: helpfulNo ?? (v === 'no' ? (prev.helpfulNo||0)+1 : prev.helpfulNo||0) };
      });
      setVotes(prev => ({ ...prev, [e.id]: userVote || v }));
    } catch (err) {
      // ignore; UI will remain unchanged
    }
  };

  const renderMarkdown = (md) => {
    try {
      let src = md || '';
      // Replace shortcodes with safe iframes
      const ytRe = /:::youtube\s+([^\n]+)\n?/gi;
      const vmRe = /:::vimeo\s+([^\n]+)\n?/gi;
      const normYouTube = (input) => {
        try {
          // extract ID from URL or raw id
          let s = String(input).trim();
          const url = new URL(s, window.location.origin);
          if (/youtube\.com|youtu\.be/i.test(url.hostname)) {
            // watch?v=ID or youtu.be/ID
            const id = url.searchParams.get('v') || url.pathname.split('/').filter(Boolean).pop();
            return id;
          }
          return s;
        } catch { return String(input).trim(); }
      };
      const normVimeo = (input) => {
        try {
          let s = String(input).trim();
          const url = new URL(s, window.location.origin);
          if (/vimeo\.com/i.test(url.hostname)) {
            const id = url.pathname.split('/').filter(Boolean).pop();
            return id;
          }
          return s;
        } catch { return String(input).trim(); }
      };
      src = src.replace(ytRe, (_m, g1) => {
        const id = DOMPurify.sanitize(normYouTube(g1), { ALLOWED_TAGS: [] });
        const embed = `<div class="video-embed"><iframe src="https://www.youtube-nocookie.com/embed/${id}" title="YouTube video" loading="lazy" frameborder="0" allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share" referrerpolicy="strict-origin-when-cross-origin" allowfullscreen sandbox="allow-same-origin allow-scripts allow-presentation"></iframe></div>`;
        return embed;
      });
      src = src.replace(vmRe, (_m, g1) => {
        const id = DOMPurify.sanitize(normVimeo(g1), { ALLOWED_TAGS: [] });
        const embed = `<div class="video-embed"><iframe src="https://player.vimeo.com/video/${id}" title="Vimeo video" loading="lazy" frameborder="0" allow="autoplay; fullscreen; picture-in-picture" referrerpolicy="strict-origin-when-cross-origin" allowfullscreen sandbox="allow-same-origin allow-scripts allow-presentation"></iframe></div>`;
        return embed;
      });
      const html = marked.parse(src);
      return { __html: DOMPurify.sanitize(html, { ADD_ATTR: ['target'], ADD_TAGS: ['iframe'] }) };
    } catch { return { __html: '' }; }
  };

  const toggleFaq = (id) => setOpenFaq(prev => ({ ...prev, [id]: !prev[id] }));

  const relatedItems = useMemo(() => {
    if (!active) return [];
    const activeTags = new Set((active.tags||[]).map(t=>String(t).toLowerCase()));
    const sameCat = active.categoryId;
    const scored = items.filter(e => e.id !== active.id && e.type==='article').map(e => {
      const tags = new Set((e.tags||[]).map(t=>String(t).toLowerCase()));
      let score = 0;
      if (sameCat && e.categoryId === sameCat) score += 1;
      for (const t of activeTags) if (tags.has(t)) score += 2;
      return { e, score };
    }).filter(x => x.score>0);
    scored.sort((a,b)=> b.score - a.score);
    return scored.slice(0,5).map(x=>x.e);
  }, [active, items]);

  return (
    <div className="home-page">
      <section style={{ width: '100vw', margin: 0, padding: 0, position:'relative', left:'-120px' }}>
        <h2 className="hero-title" style={{ textAlign: 'center', margin:'0 0 8px' }}>Wissensdatenbank</h2>
        <p className="hero-desc" style={{ margin:'0 auto 24px', textAlign:'center', maxWidth:760 }}>Antworten, Anleitungen und FAQs – schnell finden, was du brauchst.</p>
      </section>
      <section style={{ width:'100%', maxWidth:1100, margin:'0 auto', padding:'0 16px 40px 16px', color:'#fff' }}>
        <div style={{ display:'grid', gridTemplateColumns:'1.1fr 0.9fr', gap:16 }}>
          <div>
            <div style={{ background:'rgba(255,255,255,0.03)', border:'1px solid rgba(255,255,255,0.08)', borderRadius:10, padding:12, marginBottom:10 }}>
              <div style={{ display:'flex', gap:8, flexWrap:'wrap', alignItems:'center' }}>
                <input placeholder="Suche (Titel/Inhalt)" value={q} onChange={e=>setQ(e.target.value)} />
                <select value={filter.categoryId} onChange={e=>setFilter({ ...filter, categoryId: e.target.value })}>
                  <option value="">Alle Kategorien</option>
                  {cats.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
                </select>
                <select value={filter.type} onChange={e=>setFilter({ ...filter, type: e.target.value })}>
                  <option value="">Alle Typen</option>
                  <option value="article">Artikel</option>
                  <option value="faq">FAQ</option>
                </select>
                <select value={sort} onChange={e=>setSort(e.target.value)}>
                  <option value="newest">Neueste</option>
                  <option value="mostYes">Am hilfreichsten (Ja)</option>
                  <option value="bestRatio">Beste Quote (Ja/Nein)</option>
                </select>
              </div>
            </div>
            <ul style={{ marginTop:8 }}>
              {sortedItems.map(e => (
                <li key={e.id} style={{ background:'rgba(255,255,255,0.03)', border:'1px solid rgba(255,255,255,0.08)', borderRadius:10, padding:12, marginBottom:10 }}>
                  <div style={{ display:'flex', alignItems:'center', gap:10 }}>
                    <div style={{ width:10, height:10, borderRadius:3, background: e.type==='faq' ? '#7ad1ff' : '#9bc86a' }} />
                    <b style={{ fontSize:16, cursor:'pointer' }} onClick={()=> e.type==='faq' ? toggleFaq(e.id) : openEntry(e)}>{e.title}</b>
                    {e.KnowledgeCategory && <span style={{ fontSize:12, color:'#bbb' }}>· {e.KnowledgeCategory.name}</span>}
                    <span style={{ fontSize:12, color:'#bbb' }}>· {new Date(e.createdAt).toLocaleDateString()}</span>
                    {(e.helpfulYes || e.helpfulNo) ? <span style={{ fontSize:12, color:'#bbb' }}>· {e.helpfulYes||0} Ja · {e.helpfulNo||0} Nein</span> : null}
                  </div>
                  {e.excerpt && e.type!=='faq' && <div style={{ marginTop:6, color:'#ddd' }}>{e.excerpt}</div>}
                  {e.type==='faq' && openFaq[e.id] && (
                    <div style={{ marginTop:6 }}>
                      <div dangerouslySetInnerHTML={renderMarkdown(e.content || e.excerpt || '')} />
                    </div>
                  )}
                  {Array.isArray(e.tags) && e.tags.length>0 && (
                    <div style={{ marginTop:6, display:'flex', flexWrap:'wrap', gap:6 }}>
                      {e.tags.map(t => <span key={t} style={{ fontSize:11, padding:'2px 6px', borderRadius:12, border:'1px solid rgba(255,255,255,0.15)' }}>#{t}</span>)}
                    </div>
                  )}
                </li>
              ))}
              {items.length===0 && <li style={{ color:'#aaa', fontSize:12 }}>Keine Einträge gefunden.</li>}
            </ul>
          </div>
          <div>
            <div style={{ background:'rgba(255,255,255,0.03)', border:'1px solid rgba(255,255,255,0.08)', borderRadius:10, padding:12 }}>
              {!active ? (
                <div style={{ color:'#bbb' }}>Wähle links einen Eintrag aus.</div>
              ) : (
                <div>
                  <h3 style={{ marginTop:0 }}>{active.title}</h3>
                  {active.KnowledgeCategory && <div style={{ fontSize:12, color:'#bbb' }}>{active.KnowledgeCategory.name}</div>}
                  {active.author && <div style={{ fontSize:12, color:'#bbb' }}>von {active.author.displayName || active.author.username}</div>}
                  {active.content && (
                    <div style={{ marginTop:8 }} dangerouslySetInnerHTML={renderMarkdown(active.content)} />
                  )}
                  {active.type==='article' && Array.isArray(active.attachments) && active.attachments.length>0 && (
                    <div style={{ marginTop:10 }}>
                      <div style={{ fontSize:12, color:'#bbb' }}>Anhänge & Links</div>
                      <ul style={{ marginTop:6 }}>
                        {active.attachments.map((a,i)=> (
                          <li key={i}>
                            <a href={a.url} target="_blank" rel="noopener noreferrer">{a.title || a.url}</a>
                          </li>
                        ))}
                      </ul>
                    </div>
                  )}
                  <div style={{ marginTop:10, display:'flex', alignItems:'center', gap:8, fontSize:12, color:'#bbb', flexWrap:'wrap' }}>
                    <span>War dieser Artikel hilfreich?</span>
                    <button disabled={!!votes[active.id]} onClick={()=>vote(active,'yes')}>Ja</button>
                    <button disabled={!!votes[active.id]} onClick={()=>vote(active,'no')}>Nein</button>
                    {(typeof active.helpfulYes === 'number' || typeof active.helpfulNo === 'number') && (
                      <span style={{ opacity:0.9 }}>· {active.helpfulYes||0} Ja · {active.helpfulNo||0} Nein</span>
                    )}
                    {votes[active.id] && <span style={{ color:'#9bc86a' }}>Danke für dein Feedback!</span>}
                  </div>
                  {active.type==='article' && relatedItems.length>0 && (
                    <div style={{ marginTop:16 }}>
                      <div style={{ fontSize:12, color:'#bbb' }}>Verwandte Artikel</div>
                      <ul style={{ marginTop:6 }}>
                        {relatedItems.map(r => (
                          <li key={r.id}>
                            <a onClick={()=>setActive(r)} style={{ cursor:'pointer' }}>{r.title}</a>
                          </li>
                        ))}
                      </ul>
                    </div>
                  )}
                </div>
              )}
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}
